import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    CheckboxIcon,
    PlusIcon,
    StyledLabel,
    StyledModal,
    TrashIcon,
} from "@aureskonnect/react-ui";
import classnames from "classnames";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import ReactSwitch from "react-switch";
import { AvForm, AvGroup } from "availity-reactstrap-validation";
import { IconButton } from "@mui/material";
import { toast } from "react-toastify";
import { uuid } from "uuidv4";
import { mutate } from "swr";
import { useSnapshot } from "valtio";

import {
    setActions,
    setIsActionsClicked,
    setIsEdited,
    setKey,
    store as foodCourtStore,
} from "./store";
import { store } from "@components/VerticalLayout/store";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { daysOfWeek } from "@constants/index";
import AlarmClock from "@components/Common/SvgIcons/AlarmClock";
import {
    handleAddSlot,
    handleChangeSlot,
    handleChangeState,
    handleRemoveSlot,
    hasOverlaps,
    isOverlapping,
    RedisPublishFoodCourt,
    setAllWeek,
} from "./helpers";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { useFetchGlobal } from "../../../api/hooks";

export function HoursModal({ isModalOpened, setIsModalOpened, rowData }: any) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();

    const { shopID } = useSnapshot(store);
    const { isActionsClicked, isEdited } = useSnapshot(foodCourtStore);

    const getInitialOpeningHours = () =>
        Object.keys(rowData.hour).length === 0
            ? daysOfWeek.reduce((acc, day) => {
                  acc[day.key] = {
                      open: true,
                      slots: [{ from: "", to: "" }],
                  };
                  return acc;
              }, {} as any)
            : JSON.parse(JSON.stringify(rowData.hour));

    const [openingHours, setOpeningHours] = useState<any>(
        getInitialOpeningHours()
    );
    const [isAllWeek, setIsAllWeek] = useState(
        rowData.hour["7"] !== undefined ? rowData.hour["7"].allWeek : false
    );
    const [errors, setErrors] = useState<{
        [day: string]: { [index: number]: { from?: boolean; to?: boolean } };
    }>({});

    const resetState = () => {
        setOpeningHours(getInitialOpeningHours());
        setIsAllWeek(
            rowData.hour["7"] !== undefined ? rowData.hour["7"].allWeek : false
        );
        setErrors({});
    };

    async function handleValidate() {
        toast.dismiss();

        let hasEmptyFields = false;
        let hasInvalidRanges = false;
        const newErrors: any = {};

        Object.keys(openingHours).forEach((dayKey) => {
            const day = openingHours[dayKey];
            if (day.open) {
                day.slots.forEach((slot: any, index: number) => {
                    const hasEmptyFrom = !slot.from;
                    const hasEmptyTo = !slot.to;

                    if (hasEmptyFrom || hasEmptyTo) {
                        hasEmptyFields = true;
                        if (!newErrors[dayKey]) {
                            newErrors[dayKey] = {};
                        }
                        newErrors[dayKey][index] = {
                            from: hasEmptyFrom,
                            to: hasEmptyTo,
                        };
                    } else if (slot.to <= slot.from) {
                        // Check if closing time is before or equal to opening time
                        hasInvalidRanges = true;
                    }
                });
            }
        });
        if (hasEmptyFields || hasInvalidRanges || hasOverlaps(openingHours)) {
            setErrors(newErrors);
            return;
        }
        setErrors({});
        const apiUrlAdd = `${process.env.REACT_APP_API_V2_URL}/settings/general/foodCourt`;
        let data = {
            shopId: shopID,
            data: {
                [rowData.Id]: {
                    etat: rowData.state,
                    horaires: {
                        ...openingHours,
                        "7": { ...openingHours["7"], allWeek: isAllWeek },
                    },
                    remarque: rowData[t("Remark")],
                    middlewareIpAddress: rowData[t("Middleware IP address")],
                    softaveraPay_Yavin: rowData.softaveraPay_Yavin,
                },
            },
        };
        const requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
        };
        try {
            const response: any = await fetchGlobal(apiUrlAdd, requestOptions);
            if (response.error) {
                throw Error("error");
            }
            mutate(apiUrlAdd, response);
            setIsEdited(false);
            setKey(uuid());
            toast.success(
                `${t("Middleware IP address configured successfully.")!}`,
                {
                    position: toast.POSITION.TOP_CENTER,
                    theme: "colored",
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                }
            );
            setIsModalOpened(false);
            await RedisPublishFoodCourt(shopID, t, fetchGlobal);
        } catch (e) {
            toast.error(`${t("There's an error")!}`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    }

    return (
        <StyledModal
            toggle={true}
            isOpen={isModalOpened!}
            centered
            size="lg"
            scrollable
        >
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    if (isEdited === true) {
                        setIsActionsClicked(!isActionsClicked);
                        setActions(() => {
                            setIsEdited(false);
                            setIsModalOpened(false);
                            setIsEdited(false);
                            resetState();
                        });
                    } else {
                        resetState();
                        setIsModalOpened(false);
                        setIsEdited(false);
                    }
                }}
            >
                <span>{t("Opening hours")}</span>
            </ModalHeader>
            <ModalBody>
                {daysOfWeek.map((day) => {
                    const data = openingHours[day.key];
                    return (
                        <div
                            key={day.key}
                            className="mb-3 border rounded"
                            style={{
                                maxHeight: "200px", // set a fixed or max height
                                overflowY: "scroll", // enable vertical scroll
                            }}
                        >
                            <div
                                className="d-flex justify-content-between align-items-center px-3"
                                style={{
                                    backgroundColor: "#F5F5F5",
                                    height: "50px",
                                }}
                            >
                                {day.key === "7" ? (
                                    <div className="d-flex align-items-center">
                                        <ReactSwitch
                                            checked={isAllWeek}
                                            onChange={(checked: boolean) => {
                                                setIsAllWeek(checked);
                                                if (checked === true) {
                                                    setAllWeek(
                                                        openingHours,
                                                        data,
                                                        setOpeningHours
                                                    );
                                                    // Replicate errors from day 7 to all days
                                                    if (errors["7"]) {
                                                        const newErrors: any = {};
                                                        Object.keys(
                                                            openingHours
                                                        ).forEach((dayKey) => {
                                                            newErrors[
                                                                dayKey
                                                            ] = {
                                                                ...errors["7"],
                                                            };
                                                        });
                                                        setErrors(newErrors);
                                                    }
                                                }
                                            }}
                                            onColor="#00b894"
                                            offColor="#d63031"
                                            uncheckedIcon={false}
                                            checkedIcon={false}
                                            height={20}
                                            width={40}
                                        />
                                        <span className="ml-2">
                                            {t(day.label)}
                                        </span>
                                    </div>
                                ) : (
                                    <StyledLabel>{t(day.label)}</StyledLabel>
                                )}
                                <div className="d-flex align-items-center">
                                    <ReactSwitch
                                        checked={data.open}
                                        onChange={(checked: boolean) => {
                                            handleChangeState(
                                                day.key,
                                                checked,
                                                setOpeningHours,
                                                openingHours,
                                                isAllWeek,
                                                setIsAllWeek
                                            );
                                        }}
                                        onColor="#00b894"
                                        offColor="#d63031"
                                        uncheckedIcon={false}
                                        checkedIcon={false}
                                        height={20}
                                        width={40}
                                    />
                                    <span className="ml-2">
                                        {data.open ? t("Open") : t("Closed")}
                                    </span>
                                </div>
                            </div>
                            <div className="border-footer__clz" />
                            <AvForm className="p-3">
                                {data.slots.map((slot: any, i: number) => {
                                    const hasOverlap = isOverlapping(
                                        i,
                                        data.slots
                                    );
                                    // Check if closing time is before opening time
                                    const hasInvalidTimeRange =
                                        slot.from &&
                                        slot.to &&
                                        slot.to <= slot.from;
                                    return (
                                        <div
                                            key={i}
                                            className="d-flex justify-content-between"
                                        >
                                            <AvGroup>
                                                <StyledLabel
                                                    className="mt-3"
                                                    htmlFor="name"
                                                >
                                                    {t("Opening hour")}
                                                </StyledLabel>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div
                                                        style={{
                                                            position:
                                                                "relative",
                                                            width: "200px",
                                                        }}
                                                    >
                                                        <input
                                                            type="time"
                                                            defaultValue="08:00"
                                                            style={{
                                                                width: "100%",
                                                                paddingLeft:
                                                                    "35px", // space for the icon
                                                                height: "35px",
                                                                boxSizing:
                                                                    "border-box",
                                                            }}
                                                            value={slot.from}
                                                            onChange={(e) =>
                                                                handleChangeSlot(
                                                                    day.key,
                                                                    i,
                                                                    "from",
                                                                    e.target
                                                                        .value,
                                                                    setOpeningHours,
                                                                    openingHours,
                                                                    isAllWeek,
                                                                    setIsAllWeek,
                                                                    setErrors
                                                                )
                                                            }
                                                            className={classnames(
                                                                "",
                                                                {
                                                                    invalid__clz:
                                                                        hasOverlap ||
                                                                        hasInvalidTimeRange ||
                                                                        errors[
                                                                            day
                                                                                .key
                                                                        ]?.[i]
                                                                            ?.from,
                                                                }
                                                            )}
                                                        />
                                                        <span
                                                            style={{
                                                                position:
                                                                    "absolute",
                                                                left: "10px",
                                                                top: "50%",
                                                                transform:
                                                                    "translateY(-50%)",
                                                                pointerEvents:
                                                                    "none",
                                                            }}
                                                        >
                                                            <AlarmClock
                                                                width={18}
                                                                height={18}
                                                            />
                                                        </span>
                                                    </div>
                                                </div>
                                                {hasOverlap ||
                                                hasInvalidTimeRange ? (
                                                    <div
                                                        style={{
                                                            color: "#f46a6a",
                                                        }}
                                                    >
                                                        {t("Overlap detected")}
                                                    </div>
                                                ) : errors[day.key]?.[i]
                                                      ?.from ? (
                                                    <div
                                                        style={{
                                                            color: "#f46a6a",
                                                        }}
                                                    >
                                                        {t(
                                                            "Veuillez choisir une heure"
                                                        )}
                                                    </div>
                                                ) : null}
                                            </AvGroup>
                                            <AvGroup>
                                                <StyledLabel
                                                    className="mt-3"
                                                    htmlFor="name"
                                                >
                                                    {t("Closing hour")}
                                                </StyledLabel>
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div
                                                        style={{
                                                            position:
                                                                "relative",
                                                            width: "200px",
                                                        }}
                                                    >
                                                        <input
                                                            type="time"
                                                            defaultValue="08:00"
                                                            style={{
                                                                width: "100%",
                                                                paddingLeft:
                                                                    "35px", // space for the icon
                                                                height: "35px",
                                                                boxSizing:
                                                                    "border-box",
                                                            }}
                                                            value={slot.to}
                                                            onChange={(e) =>
                                                                handleChangeSlot(
                                                                    day.key,
                                                                    i,
                                                                    "to",
                                                                    e.target
                                                                        .value,
                                                                    setOpeningHours,
                                                                    openingHours,
                                                                    isAllWeek,
                                                                    setIsAllWeek,
                                                                    setErrors
                                                                )
                                                            }
                                                            className={classnames(
                                                                "",
                                                                {
                                                                    invalid__clz:
                                                                        hasOverlap ||
                                                                        hasInvalidTimeRange ||
                                                                        errors[
                                                                            day
                                                                                .key
                                                                        ]?.[i]
                                                                            ?.to,
                                                                }
                                                            )}
                                                        />
                                                        <span
                                                            style={{
                                                                position:
                                                                    "absolute",
                                                                left: "10px",
                                                                top: "50%",
                                                                transform:
                                                                    "translateY(-50%)",
                                                                pointerEvents:
                                                                    "none",
                                                            }}
                                                        >
                                                            <AlarmClock
                                                                width={18}
                                                                height={18}
                                                            />
                                                        </span>
                                                    </div>
                                                </div>
                                                {hasOverlap ||
                                                hasInvalidTimeRange ? (
                                                    <div
                                                        style={{
                                                            color: "#f46a6a",
                                                        }}
                                                    >
                                                        {t("Overlap detected")}
                                                    </div>
                                                ) : errors[day.key]?.[i]?.to ? (
                                                    <div
                                                        style={{
                                                            color: "#f46a6a",
                                                        }}
                                                    >
                                                        {t(
                                                            "Veuillez choisir une heure"
                                                        )}
                                                    </div>
                                                ) : null}
                                            </AvGroup>
                                            {data.slots.length > 1 ? (
                                                <IconButton
                                                    className="btn d-flex align-items-center"
                                                    style={{
                                                        border:
                                                            "1px solid #ced4da",
                                                        display: "flex",
                                                        width: "51px",
                                                        height: "10%",
                                                        marginTop: "6%",
                                                        borderRadius: "0%",
                                                    }}
                                                    onClick={async () => {
                                                        handleRemoveSlot(
                                                            day.key,
                                                            i,
                                                            setOpeningHours,
                                                            isAllWeek,
                                                            openingHours,
                                                            setIsAllWeek,
                                                            setErrors
                                                        );
                                                    }}
                                                >
                                                    <TrashIcon
                                                        style={{
                                                            height: "50%",
                                                            width: "60%",
                                                        }}
                                                        fill="red"
                                                    />
                                                </IconButton>
                                            ) : (
                                                <div className="ml-5"></div>
                                            )}
                                            {i === data.slots.length - 1 ? (
                                                <IconButton
                                                    className="btn d-flex align-items-center"
                                                    style={{
                                                        border:
                                                            "1px solid #ced4da",
                                                        display: "flex",
                                                        width: "7%",
                                                        height: "7%",
                                                        marginTop: "6%",
                                                        borderRadius: "0%",
                                                    }}
                                                    onClick={() =>
                                                        handleAddSlot(
                                                            day.key,
                                                            setOpeningHours,
                                                            isAllWeek,
                                                            openingHours,
                                                            setIsAllWeek
                                                        )
                                                    }
                                                >
                                                    <PlusIcon
                                                        style={{
                                                            height: "50%",
                                                            width: "60%",
                                                        }}
                                                    />
                                                </IconButton>
                                            ) : (
                                                <div className="ml-5"></div>
                                            )}
                                        </div>
                                    );
                                })}
                            </AvForm>
                        </div>
                    );
                })}
            </ModalBody>
            <ModalFooter className="border-top-0 ">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    onClick={() => {
                        if (isEdited === true) {
                            setIsActionsClicked(!isActionsClicked);
                            setActions(() => {
                                setIsEdited(false);
                                setIsModalOpened(false);
                                setIsEdited(false);
                                resetState();
                            });
                        } else {
                            resetState();
                            setIsModalOpened(false);
                            setIsEdited(false);
                        }
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2"
                    variant="primary"
                    onClick={handleValidate}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
